import { forwardRef, useEffect } from "react";
import JobRunnerTabs from "./JobRunnerTabs";
import JobRunnerToolBox from "./JobRunnerToolBox";
import JobRunnerListBox from "./JobRunnerListBox";
import JobRunnerEditorPanel from "./JobRunnerEditorPanel";
import JobRunnerDialogs from "./JobRunnerDialogs";
import { useJobRunnerDialogs } from "./hooks/useJobRunnerDialogs";
import { useJobRunnerActions } from "./hooks/useJobRunnerActions";
import { useJobRunnerApis } from "./hooks/useJobRunnerApis";
import "./JobRunner.scss";

export enum JobRunnerTabTypes {
  CONFIG = "Config",
  CODE = "Code",
  TEMPLATE = "Template",
}

const JobRunner = forwardRef<HTMLDivElement>((props, ref) => {
  const dialogs = useJobRunnerDialogs();
  const { managementApi } = useJobRunnerApis();

  const actions = useJobRunnerActions(
    dialogs.setOpenDialog,
    dialogs.closeDialog
  );

  useEffect(() => {
    actions.fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isCodeButtonEnabled =
    !actions.selectedJob ||
    (actions.currentTab === JobRunnerTabTypes.CODE &&
      actions.currentTask?.codeFileId != null) ||
    actions.currentTab === JobRunnerTabTypes.CONFIG;

  return (
    <div ref={ref}>
      <div className="data-writer">
        <JobRunnerListBox
          listLoad={actions.listLoad}
          jobs={actions.jobs}
          selectedJob={actions.selectedJob}
          lastExecutions={actions.lastExecutions}
          handleJobSelection={actions.handleJobSelection}
          openJobLogs={(job) => actions.openLogsDialogWithFetch(job.taskId)}
        />
        <JobRunnerToolBox
          tabType={actions.currentTab}
          isConfigEdited={actions.isConfigEdited}
          isCodeEdited={actions.isCodeEdited}
          isTemplateInfoEdited={actions.isTemplateInfoEdited}
          isCodeButtonEnabled={isCodeButtonEnabled}
          saveTaskConfig={actions.saveTaskConfig}
          saveTaskCode={actions.saveTaskCode}
          saveTemplateInfo={actions.saveTemplateInfo}
          selectedJob={actions.selectedJob}
          setIsHistoryDialogVisible={dialogs.openHistoryDialog}
          setIsAssignNewCodeVisible={actions.openAssignCodeDialogWithFetch}
          fetchCodeFilesList={actions.fetchCodeFilesList}
          openTestDataDialog={dialogs.openReferenceTimePickerDialog}
          openLogsDialog={() => actions.openLogsDialogWithFetch()}
          openAddNewJobDialog={dialogs.openAddJobDialog}
          openDeleteJobDialog={dialogs.openDeleteJobDialog}
          runTaskManually={actions.runTask}
          handleToggleJobRunner={actions.handleToggleJobRunner}
          updateJob={actions.updateJob}
        />
        <div className="right-side">
          <div className="tab-view-window">
            <JobRunnerTabs
              tabType={actions.currentTab}
              handleTabChange={actions.handleTabChange}
            />
            <div className="json-window">
              <JobRunnerEditorPanel
                currentTab={actions.currentTab}
                currentTask={actions.currentTask}
                templateInfo={actions.templateInfo}
                taskCode={actions.taskCode}
                encoderKey={actions.encoderKey}
                encoderRef={actions.encoderRef}
                jobRunnerViewRef={actions.jobRunnerViewRef}
                managementApi={managementApi}
                readScript={actions.readScript}
                setReadScript={actions.setReadScript}
                codeFilesList={actions.codeFilesList}
                selectedCodeFile={actions.selectedCodeFile}
                setSelectedCodeFile={actions.setSelectedCodeFile}
                setFileToUpload={actions.setFileToUpload}
                selectedJob={actions.selectedJob}
                loadTaskCode={actions.loadTaskCode}
                uploadTaskCode={actions.uploadTaskCode}
                setIsCreateFileDialogVisible={dialogs.openCreateFileDialog}
                createEmptyFileForJob={actions.createEmptyFileForJob}
                handleScriptChange={actions.handleScriptChange}
                handleEncoderConfigUpdate={actions.handleEncoderConfigUpdate}
                writerChanged={actions.writerChanged}
                saveTaskConfig={actions.saveTaskConfig}
                saveTemplateInfo={actions.saveTemplateInfo}
              />
            </div>
          </div>
        </div>
      </div>

      <JobRunnerDialogs
        openDialog={dialogs.openDialog}
        closeDialog={dialogs.closeDialog}
        selectedJob={actions.selectedJob}
        openTestDataDialogWithFetch={actions.openTestDataDialogWithFetch}
        currentTestData={actions.currentTestData}
        currentExecutionsData={actions.currentExecutionsData}
        handleFetchExecutions={actions.handleFetchExecutions}
        logsInitialTaskId={actions.logsInitialTaskId}
        newFileName={actions.newFileName}
        setNewFileName={actions.setNewFileName}
        createBlankPythonFile={actions.createBlankPythonFile}
        currentTab={actions.currentTab}
        codeFileId={actions.currentTask?.codeFileId ?? undefined}
        codeFilesList={actions.codeFilesList}
        selectedCodeFile={actions.selectedCodeFile}
        setSelectedCodeFile={actions.setSelectedCodeFile}
        setFileToUpload={actions.setFileToUpload}
        loadTaskCode={actions.loadTaskCode}
        uploadTaskCode={actions.uploadTaskCode}
        openCreateFileDialog={dialogs.openCreateFileDialog}
        createEmptyFileForJob={actions.createEmptyFileForJob}
        jobs={actions.jobs}
        handleAddNewJob={actions.handleAddNewJob}
        handleRemoveJob={actions.handleRemoveJob}
      />
    </div>
  );
});

export default JobRunner;
